/**
 * Reusable SEO component — injects <head> meta tags via react-helmet-async.
 *
 * Usage:
 *   <SEO view="landing" />
 *   <SEO view="solo" />
 *   <SEO title="Custom" description="Override" />
 *
 * Reads from centralized seo.config.js. Falls back to DEFAULTS for any
 * missing field. Injects JSON-LD structured data on the landing view.
 */

import { Helmet } from 'react-helmet-async';
import { DEFAULTS, VIEW_META, SITE_URL } from './seo.config';
import { websiteSchema, gameSchema, breadcrumbSchema } from './structured-data';

export default function SEO({ view = 'landing', title, description }) {
  const viewMeta = VIEW_META[view] || {};
  const pageTitle = title || viewMeta.title || DEFAULTS.title;
  const pageDesc = description || viewMeta.description || DEFAULTS.description;
  const canonical = `${SITE_URL}${viewMeta.canonicalPath || '/'}`;

  // Only inject structured data on the landing page (main entry point)
  const jsonLd = view === 'landing'
    ? [
        websiteSchema(),
        gameSchema(),
        breadcrumbSchema([{ name: 'Home', path: '/' }]),
      ]
    : null;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={DEFAULTS.keywords} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content={DEFAULTS.ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={DEFAULTS.ogImage} />
      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:locale" content={DEFAULTS.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={DEFAULTS.twitterCard} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={DEFAULTS.ogImage} />

      {/* JSON-LD structured data */}
      {jsonLd && jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
